/**
 * Demand Intelligence Synthesis Dashboard
 *
 * Main entry point for the synthesis layer UI. Contains:
 * - Market switcher (tab pills)
 * - Executive overview (KPI cards)
 * - Gap queue table
 * - Vocabulary gaps
 * - Money leak detector
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CoverageBar } from '@/components/synthesis/CoverageBar';
import { AuthorityBadge } from '@/components/synthesis/AuthorityBadge';
import { ClusterBadge } from '@/components/synthesis/ClusterBadge';
import {
  fetchMarkets, fetchClusters, fetchGapQueue, fetchVocabularyGaps,
  fetchPaidOrganicOverlap, fetchCorpusStats, updateGapStatus,
  approveVocabularyGap, triggerPipeline,
  type Market, type GapQueueItem, type VocabularyGap, type PaidOrganicOverlap,
} from '@/services/synthesis/synthesisApi';
import {
  TrendingUp, DollarSign, BarChart3, Zap, ChevronDown, ChevronRight,
  ArrowRight, RefreshCw, Check, Clock, AlertTriangle
} from 'lucide-react';

/** Safely parse any value to a number (handles null, undefined, strings from PostgreSQL NUMERIC) */
const num = (v: unknown): number => {
  if (v == null) return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

export default function DemandSynthesisDashboard() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeMarket, setActiveMarket] = useState<string | null>(searchParams.get('market'));
  const [activeTab, setActiveTab] = useState('gap-queue');
  const queryClient = useQueryClient();

  // ── Markets ──────────────────────────────────────────────────────────
  const { data: markets = [] } = useQuery({
    queryKey: ['synthesis-markets', siteId],
    queryFn: () => fetchMarkets(siteId!),
    enabled: !!siteId,
  });

  useEffect(() => {
    if (!activeMarket && markets.length > 0) {
      setActiveMarket(markets[0].market);
    }
  }, [markets, activeMarket]);

  useEffect(() => {
    if (activeMarket) {
      setSearchParams({ market: activeMarket });
    }
  }, [activeMarket, setSearchParams]);

  // ── Corpus stats ─────────────────────────────────────────────────────
  const { data: corpusStats } = useQuery({
    queryKey: ['synthesis-corpus-stats', siteId],
    queryFn: () => fetchCorpusStats(siteId!),
    enabled: !!siteId,
  });

  // ── Clusters for active market ───────────────────────────────────────
  const { data: clusters = [] } = useQuery({
    queryKey: ['synthesis-clusters', siteId, activeMarket],
    queryFn: () => fetchClusters(siteId!, activeMarket || undefined),
    enabled: !!siteId,
  });

  // ── Gap queue ────────────────────────────────────────────────────────
  const { data: gapQueue = [] } = useQuery({
    queryKey: ['synthesis-gap-queue', siteId, activeMarket],
    queryFn: () => fetchGapQueue(siteId!, { market: activeMarket || undefined }),
    enabled: !!siteId,
  });

  // ── Vocabulary gaps ──────────────────────────────────────────────────
  const { data: vocabGaps = [] } = useQuery({
    queryKey: ['synthesis-vocab-gaps', siteId],
    queryFn: () => fetchVocabularyGaps(siteId!, { synonymOnly: true }),
    enabled: !!siteId && activeTab === 'vocabulary',
  });

  // ── Paid-organic overlap ─────────────────────────────────────────────
  const { data: overlapData } = useQuery({
    queryKey: ['synthesis-overlap', siteId],
    queryFn: () => fetchPaidOrganicOverlap(siteId!),
    enabled: !!siteId && activeTab === 'money-leaks',
  });

  // ── Pipeline trigger ─────────────────────────────────────────────────
  const pipelineMutation = useMutation({
    mutationFn: () => triggerPipeline(siteId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synthesis-corpus-stats'] });
    },
  });

  if (!siteId) {
    return <div className="p-8 text-muted-foreground">Select a site to view demand intelligence.</div>;
  }

  // ── KPI calculations ─────────────────────────────────────────────────
  const totalEurValue = clusters.reduce((s, c) => s + num(c.total_commercial_value_eur), 0);
  const avgCoverage = clusters.length > 0
    ? clusters.reduce((s, c) => s + num(c.coverage_pct), 0) / clusters.length
    : 0;
  const totalReclaimable = overlapData?.totalReclaimable || 0;
  const trendingCount = clusters.filter(c => c.trending).length;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Demand Intelligence</h1>
          <p className="text-muted-foreground text-sm">
            {corpusStats ? `${corpusStats.total.toLocaleString()} signals · ${corpusStats.embeddingPct}% embedded · ${clusters.length} clusters` : 'Loading...'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => pipelineMutation.mutate()}
          disabled={pipelineMutation.isPending}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${pipelineMutation.isPending ? 'animate-spin' : ''}`} />
          Run Pipeline
        </Button>
      </div>

      {/* Market switcher */}
      {markets.length > 0 && (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {markets.map(m => (
            <button
              key={m.market}
              onClick={() => setActiveMarket(m.market)}
              className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors ${
                activeMarket === m.market
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              {m.store}/{m.language}
              {m.total_eur_value > 0 && (
                <span className="ml-1 text-xs opacity-75">€{Math.round(m.total_eur_value).toLocaleString()}</span>
              )}
              {m.trending && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-amber-500 inline-block animate-pulse" />}
            </button>
          ))}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" /> EUR Opportunity
            </div>
            <div className="text-2xl font-bold">€{Math.round(totalEurValue).toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <BarChart3 className="h-4 w-4" /> Demand Coverage
            </div>
            <div className="text-2xl font-bold">{Math.round(avgCoverage)}%</div>
            <CoverageBar value={avgCoverage} showLabel={false} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-red-500 mb-1">
              <AlertTriangle className="h-4 w-4" /> Reclaimable Spend
            </div>
            <div className="text-2xl font-bold text-red-600">€{Math.round(totalReclaimable).toLocaleString()}/mo</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" /> Clusters
            </div>
            <div className="text-2xl font-bold">{clusters.length}</div>
            {trendingCount > 0 && <span className="text-xs text-amber-600">{trendingCount} trending</span>}
          </CardContent>
        </Card>
      </div>

      {/* Main tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="gap-queue">Gap Queue ({gapQueue.length})</TabsTrigger>
          <TabsTrigger value="clusters">Clusters ({clusters.length})</TabsTrigger>
          <TabsTrigger value="vocabulary">Vocabulary Gaps</TabsTrigger>
          <TabsTrigger value="money-leaks">Money Leaks</TabsTrigger>
        </TabsList>

        <TabsContent value="gap-queue">
          <GapQueueTable items={gapQueue} siteId={siteId} />
        </TabsContent>

        <TabsContent value="clusters">
          <ClusterList clusters={clusters} />
        </TabsContent>

        <TabsContent value="vocabulary">
          <VocabularyGapTable items={vocabGaps} siteId={siteId} />
        </TabsContent>

        <TabsContent value="money-leaks">
          <MoneyLeakTable
            items={overlapData?.overlaps || []}
            totalReclaimable={overlapData?.totalReclaimable || 0}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Gap Queue Table ──────────────────────────────────────────────────────

function GapQueueTable({ items, siteId }: { items: GapQueueItem[]; siteId: string }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => updateGapStatus(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['synthesis-gap-queue'] }),
  });

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No open gaps for this market. Run the synthesis pipeline to discover demand gaps.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-3 w-8"></th>
              <th className="text-left p-3">Cluster</th>
              <th className="text-right p-3">Gap Score</th>
              <th className="text-right p-3">EUR/mo</th>
              <th className="text-left p-3 w-40">Coverage</th>
              <th className="text-right p-3">Competitors</th>
              <th className="text-left p-3">Action</th>
              <th className="text-right p-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <React.Fragment key={item.id}>
                <tr
                  className="border-b hover:bg-muted/50 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                >
                  <td className="p-3">
                    {expandedId === item.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{item.cluster_label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {(item.top_unanswered_queries || []).slice(0, 2).map((q: any) => q.query).join(' · ')}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <span className={`font-bold ${
                      num(item.gap_score) >= 75 ? 'text-red-600' : num(item.gap_score) >= 40 ? 'text-amber-600' : 'text-green-600'
                    }`}>
                      {num(item.gap_score).toFixed(1)}
                    </span>
                  </td>
                  <td className="p-3 text-right font-medium">
                    €{Math.round(item.total_commercial_value_eur || 0).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <CoverageBar value={item.coverage_pct || 0} />
                  </td>
                  <td className="p-3 text-right">
                    <span className={item.competitor_cover_rate >= 60 ? 'text-red-500' : 'text-muted-foreground'}>
                      {Math.round(item.competitor_cover_rate || 0)}%
                    </span>
                  </td>
                  <td className="p-3">
                    <AuthorityBadge action={item.suggested_action} />
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        statusMutation.mutate({ id: item.id, status: 'in_progress' });
                      }}
                    >
                      <Clock className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
                {expandedId === item.id && (
                  <tr className="bg-muted/50">
                    <td colSpan={8} className="p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-sm mb-2">Top Unanswered Queries</h4>
                          <ul className="space-y-1">
                            {(item.top_unanswered_queries || []).map((q: any, i: number) => (
                              <li key={i} className="text-sm flex justify-between">
                                <span>{q.query}</span>
                                {q.value && <span className="text-muted-foreground">€{Math.round(q.value)}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="space-y-2">
                          <div className="text-sm">
                            <span className="text-muted-foreground">Authority: </span>
                            <span className="font-medium">{item.dominant_authority}</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground">Status: </span>
                            <span className="font-medium">{item.status}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Cluster List ─────────────────────────────────────────────────────────

function ClusterList({ clusters }: { clusters: any[] }) {
  if (clusters.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No clusters yet. Run the synthesis pipeline to discover demand clusters.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      {clusters.map(c => (
        <Card key={c.id}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {c.label || 'Unlabelled'}
              {c.trending && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
            </CardTitle>
            <p className="text-xs text-muted-foreground">{c.market} · {c.member_count} queries</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-muted-foreground text-xs">EUR value</div>
                <div className="font-bold">€{Math.round(c.total_commercial_value_eur || 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Coverage</div>
                <CoverageBar value={c.coverage_pct || 0} height={3} />
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Gap score</div>
                <div className="font-bold">{num(c.gap_score).toFixed(1)}</div>
              </div>
            </div>
            {c.top_queries && c.top_queries.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <div className="text-xs text-muted-foreground mb-1">Top queries</div>
                {c.top_queries.slice(0, 3).map((q: any, i: number) => (
                  <div key={i} className="text-xs text-muted-foreground truncate">{q.query}</div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Vocabulary Gap Table ─────────────────────────────────────────────────

function VocabularyGapTable({ items, siteId }: { items: VocabularyGap[]; siteId: string }) {
  const queryClient = useQueryClient();
  const approveMutation = useMutation({
    mutationFn: (id: string) => approveVocabularyGap(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['synthesis-vocab-gaps'] }),
  });

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No vocabulary gaps detected. Check back after the next nightly pipeline run.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-3">On-Site Term</th>
              <th className="text-center p-3 w-8"></th>
              <th className="text-left p-3">Google Term</th>
              <th className="text-right p-3">Similarity</th>
              <th className="text-right p-3">On-Site Count</th>
              <th className="text-right p-3">Google Volume</th>
              <th className="text-right p-3">CPC</th>
              <th className="text-right p-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} className="border-b hover:bg-muted/50">
                <td className="p-3 font-medium">{item.onsite_term}</td>
                <td className="p-3 text-center"><ArrowRight className="h-4 w-4 text-muted-foreground" /></td>
                <td className="p-3">{item.google_term}</td>
                <td className="p-3 text-right text-muted-foreground">{(num(item.cosine_similarity) * 100).toFixed(0)}%</td>
                <td className="p-3 text-right">{item.onsite_search_count?.toLocaleString()}</td>
                <td className="p-3 text-right">{item.google_monthly_volume?.toLocaleString() || '-'}</td>
                <td className="p-3 text-right">{item.google_cpc_eur ? `€${item.google_cpc_eur}` : '-'}</td>
                <td className="p-3 text-right">
                  {item.synonym_approved ? (
                    <Badge variant="outline"><Check className="h-3 w-3 mr-1" /> Approved</Badge>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => approveMutation.mutate(item.id)}
                      disabled={approveMutation.isPending}
                    >
                      Approve
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Money Leak Table ─────────────────────────────────────────────────────

function MoneyLeakTable({ items, totalReclaimable }: { items: PaidOrganicOverlap[]; totalReclaimable: number }) {
  return (
    <div className="space-y-4">
      {/* Reclaimable gauge */}
      <Card>
        <CardContent className="pt-6 text-center">
          <div className="text-4xl font-bold text-red-600">€{Math.round(totalReclaimable).toLocaleString()}/mo</div>
          <p className="text-sm text-muted-foreground mt-1">
            {items.filter(i => i.overlap_type === 'full_overlap').length} queries · positions 1–3 organic · active bids
          </p>
        </CardContent>
      </Card>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No paid-organic overlap detected. Requires both GSC and Google Ads data.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3">Query</th>
                  <th className="text-right p-3">Organic Pos.</th>
                  <th className="text-right p-3">Impressions</th>
                  <th className="text-right p-3">CPC (EUR)</th>
                  <th className="text-right p-3">Est. Spend</th>
                  <th className="text-right p-3">Reclaimable</th>
                  <th className="text-left p-3">Type</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr
                    key={item.id}
                    className={`border-b ${
                      item.overlap_type === 'full_overlap' ? 'bg-red-500/10' :
                      item.overlap_type === 'partial_overlap' ? 'bg-amber-500/10' : ''
                    }`}
                  >
                    <td className="p-3 font-medium">{item.query}</td>
                    <td className="p-3 text-right">{num(item.gsc_position).toFixed(1)}</td>
                    <td className="p-3 text-right">{item.gsc_impressions_30d?.toLocaleString()}</td>
                    <td className="p-3 text-right">€{num(item.ads_avg_cpc_eur).toFixed(2)}</td>
                    <td className="p-3 text-right">€{Math.round(item.estimated_monthly_spend_eur || 0).toLocaleString()}</td>
                    <td className="p-3 text-right font-bold text-red-600">
                      €{Math.round(item.reclaimable_eur_per_month || 0).toLocaleString()}
                    </td>
                    <td className="p-3">
                      <Badge variant={item.overlap_type === 'full_overlap' ? 'destructive' : 'secondary'}>
                        {item.overlap_type}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
